import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Paperclip, Mail, FileText, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import axiosClient from '../../api/axiosClient';
import toast from 'react-hot-toast';

export default function EnquiryChatModal({ isOpen, onClose, enquiry }) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [messageType, setMessageType] = useState('message');
  const [sendMail, setSendMail] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && enquiry) {
      fetchChats();
    } else {
      setChats([]);
      setMessage('');
      setFile(null);
      setMessageType('message');
      setSendMail(false);
    }
  }, [isOpen, enquiry]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chats]);

  const fetchChats = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`/admin/enquiries/${enquiry.id}/chats`);
      setChats(res.data.chats || []);
    } catch (err) {
      toast.error('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() && !file) return;

    setSending(true);
    const formData = new FormData();
    if (message) formData.append('message', message);
    if (file) formData.append('file', file);
    formData.append('type', messageType);
    formData.append('sendMail', sendMail.toString());

    try {
      const res = await axiosClient.post(`/admin/enquiries/${enquiry.id}/chats`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.data?.chat) {
        setChats(prev => [...prev, res.data.chat]);
      }
      setMessage('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (sendMail) toast.success('Sent and emailed to client!');
      else toast.success('Message saved');
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen || !enquiry) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-[#1e2336] rounded-2xl w-full max-w-2xl flex flex-col max-h-[85vh] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#151928]">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Chat: {enquiry.first_name} {enquiry.last_name}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">{enquiry.subject}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Chat History */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50 dark:bg-transparent">
          {/* Initial Enquiry Message (Context) */}
          <div className="flex flex-col items-start">
            <div className="text-xs text-slate-500 mb-1 ml-1 font-medium flex items-center gap-1">
              Client <span className="bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-[10px] uppercase">Original Enquiry</span>
            </div>
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 rounded-2xl rounded-tl-sm text-sm text-slate-700 dark:text-slate-300 shadow-sm max-w-[85%] whitespace-pre-wrap">
              {enquiry.message}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading chat history...</div>
          ) : (
            chats.map((chat) => {
              const isAdmin = chat.sender_type === 'admin';
              return (
                <div key={chat.id} className={`flex flex-col ${isAdmin ? 'items-end' : 'items-start'}`}>
                  <div className={`text-xs text-slate-500 mb-1 font-medium flex items-center gap-1 ${isAdmin ? 'mr-1' : 'ml-1'}`}>
                    {isAdmin ? 'Admin' : 'Client'}
                    {chat.message_type === 'reminder' && (
                      <span className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded text-[10px] uppercase flex items-center gap-1">
                        <AlertCircle size={10} /> Reminder
                      </span>
                    )}
                    {chat.is_emailed && (
                      <span className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded text-[10px] uppercase flex items-center gap-1">
                        <Mail size={10} /> Emailed
                      </span>
                    )}
                    <span className="text-slate-400 font-normal ml-1">{chat.created_at ? new Date(chat.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}</span>
                  </div>
                  
                  <div className={`p-3 rounded-2xl text-sm shadow-sm max-w-[85%] whitespace-pre-wrap ${
                    isAdmin 
                      ? 'bg-primary-600 text-white rounded-tr-sm' 
                      : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-tl-sm'
                  }`}>
                    {chat.message}
                    
                    {chat.file_id && (
                      <a 
                        href={`${import.meta.env.VITE_API_URL || 'http://localhost:4000/api'}/admin/enquiries/files/${chat.file_id}`} 
                        target="_blank" 
                        rel="noreferrer"
                        className={`mt-2 flex items-center gap-2 p-2 rounded-lg text-xs font-medium border ${
                          isAdmin 
                            ? 'bg-primary-700 border-primary-500 text-primary-50 hover:bg-primary-800' 
                            : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600'
                        } transition-colors`}
                      >
                        <FileText size={14} />
                        {chat.file_name}
                      </a>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-[#1e2336]">
          <form onSubmit={handleSend} className="space-y-3">
            {/* Options Row */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input 
                    type="radio" 
                    name="type" 
                    value="message" 
                    checked={messageType === 'message'}
                    onChange={() => setMessageType('message')}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  Message
                </label>
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input 
                    type="radio" 
                    name="type" 
                    value="reminder" 
                    checked={messageType === 'reminder'}
                    onChange={() => setMessageType('reminder')}
                    className="text-amber-600 focus:ring-amber-500"
                  />
                  Reminder
                </label>
              </div>
              
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={sendMail}
                  onChange={(e) => setSendMail(e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <Mail size={14} className={sendMail ? 'text-blue-500' : 'text-slate-400'} />
                Send as Email
              </label>
            </div>

            {/* Input Row */}
            <div className="flex items-end gap-2">
              <div className="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
                {file && (
                  <div className="px-3 pt-2 pb-1 flex items-center justify-between bg-primary-50 dark:bg-primary-900/20 border-b border-primary-100 dark:border-primary-800">
                    <span className="text-xs font-medium text-primary-700 dark:text-primary-400 flex items-center gap-1">
                      <Paperclip size={12} /> {file.name}
                    </span>
                    <button type="button" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-slate-400 hover:text-red-500">
                      <X size={14} />
                    </button>
                  </div>
                )}
                <textarea 
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="w-full bg-transparent px-4 py-3 text-sm outline-none resize-none dark:text-white"
                  rows="2"
                />
              </div>
              
              <div className="flex flex-col gap-2">
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden" 
                />
                <button 
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-3 text-slate-500 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                  title="Attach File"
                >
                  <Paperclip size={20} />
                </button>
                <button 
                  type="submit"
                  disabled={sending || (!message.trim() && !file)}
                  className="p-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-xl shadow-sm transition-colors flex items-center justify-center"
                >
                  <Send size={20} className={message.trim() || file ? 'translate-x-0.5 -translate-y-0.5 transition-transform' : ''} />
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
